import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, SelectChangeEvent, TextField } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcDialogTitleClose, MprcFormSubHeader, MprcLink, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcButton } from "components/MprcButton";
import { MprcContainer } from "components/MprcContainer";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { AutoRegisterSettingsResponse, CreateStandardAccountPayload, CustomFieldValueDto, FieldValueItem, WorkplaceDto } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import useCheckPassword from "views/foundation/user/hooks/useCheckPassword";
import { WorkplaceAutoComplete } from "views/foundation/workplace/components/WorkplaceAutoComplete";
import { AgreementCheckbox } from "views/public/LoginPage/AgreementCheckbox";

////////////////////////////////////////////////////////////////////////////////

type Account = {
    name: string;
    displayName: string;
    email: string | null;
    workplaceText?: string | null;
    workplaceId?: WorkplaceDto | null;
    password: string;
    confirmPassword: string;
    customFields: CustomFieldValueDto[];
};

interface IValidationFields {
    displayName?: string[] | null,
    name?: string[] | null,
    password?: string[] | null,
    confirmPassword?: string[] | null,
    customFields: { [key: string]: string[] | undefined | null, };
    email?: string[] | null,
    workplaceText?: string[] | null,
    workplaceId?: string[] | null,
}

interface IPros {
    onClose: () => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const AutoRegisterDialog: React.FC<IPros> = (props) => {
    const { onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation();

    const [openAgree, setOpenAgree] = useState<boolean>(false);
    const [readAgree, setReadAgree] = useState<boolean>(false);
    const [showPass, setShowPass] = useState<boolean>(false);
    const [agree, setAgreement] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewErrors, setViewErrors] = useState<any>();
    const [initializeErrors, setInitializeErrors] = useState<any>();
    const [settings, setSettings] = useState<AutoRegisterSettingsResponse>({
        agreementMustBeAssigned: false, emailIsRequired: false, emailSenderAddress: "", emailSenderName: "", identifiers: [], isWorkplaceFreeText: true, sendWelcomeEmail: false, workplaceLabel: "", workplaceRequired: false,
        passwordAllowNameParts: true, passwordMinLength: 0, passwordRequiresOneNumber: false, passwordRequiresOneSymbol: false, passwordRequiresOneUpperLetter: false
    });

    const [formValidation, setFormValidation] = useState<IValidationFields>(
        {
            displayName: undefined,
            name: undefined,
            password: undefined,
            confirmPassword: undefined,
            customFields: {},
            email: undefined,
            workplaceText: undefined,
            workplaceId: undefined,
        });

    const [register, setRegister] = useState<Account>({
        displayName: "",
        name: "",
        password: "",
        confirmPassword: "",
        customFields: [],
        email: "",
        workplaceText: "",
        workplaceId: null
    });

    const { checkPassword } = useCheckPassword(settings, register.name);

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const getSaveCommand = (): CreateStandardAccountPayload => {
        var dto = {} as CreateStandardAccountPayload;

        LibUtils.copyProperties(register, dto,
            (prop, value) => {
                switch (prop) {
                    case "confirmPassword":
                        return undefined;
                    case "customFields":
                        return { prop, value: register.customFields.map((item) => ({ fieldId: item.fieldId, fieldValue: item.fieldValue } as FieldValueItem)) };
                    default:
                        return { prop, value };
                }
            }
        );

        dto.preferredLocale = navigator.language;
        dto.workplaceId = register.workplaceId?.workplaceId ?? null;

        return dto;
    };


    ///
    function handleUpdateRecord(name: keyof Account, v: any, customFieldValidation: any = null) {

        const newState = { ...register, [name]: v };
        let newFormValidation = formValidation!;

        if (formValidation.hasOwnProperty(name)) {

            switch (name) {
                case "password":
                case "confirmPassword":
                    const np = checkPassword(newState.password);
                    newFormValidation.password = (np.length == 0) ? null : np;

                    newFormValidation.confirmPassword = (newState.password.length > 0 && newState.password != newState.confirmPassword)
                        ? [t("Auth.Register.ConfirmPassWrong")]
                        : null;
                    break;

                case "customFields":
                    newFormValidation.customFields = {
                        ...formValidation.customFields,
                        ...customFieldValidation
                    };
                    break;
                default:
                    newFormValidation = { ...newFormValidation, [name]: v == "" ? [t("Auth.Register.RequiredField")] : null };
            }
        }

        setRegister(newState);
        setFormValidation(state => ({ ...state, ...newFormValidation }));
    }

    ///
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent) => {
        const inputName = event.target.name as keyof Account;
        handleUpdateRecord(inputName, event.target.value);
    };

    const handleUpdateCustomField = (field: CustomFieldValueDto, value: any, isValid: boolean) => {

        let newCustomFields = register.customFields?.map((f) => {
            if (f.fieldId === field.fieldId) {
                f.fieldValue = value;
            }
            return f;
        });

        var cfValidation = {
            ...formValidation.customFields,
            [field.fieldId]: isValid ? undefined : null
        };

        handleUpdateRecord("customFields", newCustomFields, cfValidation);
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);

        const fe = r.fieldsWithError ?? {};
        setFormValidation({
            ...formValidation,
            displayName: fe["displayName"] || null,
            email: fe["email"] || null,
            name: fe["name"] || null,
            password: fe["password"] || null,
            workplaceId: fe["workplaceId"] || null,
            workplaceText: fe["workplaceText"] || null,
        });

        if (r.otherErrors) setViewErrors(r.otherErrors);

        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.account.autoRegisterSettings();

                setSettings(result.data);

                var cf: any = {};
                var rcf: CustomFieldValueDto[] = [];

                result.data.identifiers.forEach((v) => {
                    cf[v.fieldId] = v.isRequired ? null : undefined;
                    rcf.push({ ...v, fieldValue: null });
                });

                setFormValidation({
                    ...formValidation,
                    customFields: cf,
                    workplaceId: result.data.workplaceRequired && !result.data.isWorkplaceFreeText ? undefined : null,
                    workplaceText: result.data.workplaceRequired && result.data.isWorkplaceFreeText ? undefined : null
                });

                setRegister({ ...register, customFields: rcf });
            }
            catch (e) {
                setInitializeErrors(e);
            }

            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);


    // Ao salvar um DTO:
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.account.createStandardAccount(getSaveCommand());
                enqueueSnackbar(result.data.confirmationRequired
                    ? t("Auth.Register.RegisterSuccessConfirmation")
                    : t("Auth.Register.RegisterSuccess"),
                    { variant: "success" });
                onClose();

            } catch (e) {
                resolveErrors(e);
            }

            setSaving(false);
        }

        if (saving) {
            postData();
        }
        else {
            setTimeout(() => {
                document.querySelector<HTMLInputElement>("input[aria-invalid='true']")?.focus();
            }, 500);
        }

    }, [saving]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////
    const canSave = useMemo(() =>
        Object.entries(formValidation).filter(([key, v]) => key != "customFields" && key != "workplaceId" && key != "workplaceText" && v !== null).length == 0
        &&
        Object.entries(formValidation.customFields).filter(([key, v]) => v === null).length == 0
        &&
        (formValidation.workplaceText === null || formValidation.workplaceId === null)
        &&
        (!settings.agreementMustBeAssigned || agree)
        , [formValidation]);

    const renderForm = () => {
        return (
            <MprcContainer>
                <MprcContainer>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        name="name"
                        label={t("Auth.Register.NameLabel")}
                        margin="dense"
                        value={register.name}
                        onChange={(e) => handleChange(e)}
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 }
                            }
                        }}
                        error={!!formValidation.name}
                        helperText={<MprcStringToHtml text={formValidation?.name} />}
                    />
                    <TextField
                        fullWidth
                        required
                        name="displayName"
                        label={t("Auth.Register.DisplayNameLabel")}
                        value={register.displayName}
                        onChange={(e) => handleChange(e)}
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 }
                            }
                        }}
                        error={!!formValidation.displayName}
                        helperText={<MprcStringToHtml text={formValidation?.displayName} />}
                    />

                    <TextField
                        fullWidth
                        required
                        name="email"
                        label={t("Auth.Register.EmailLabel")}
                        value={register.email}
                        onChange={(e) => handleChange(e)}
                        slotProps={{
                            input: {
                                inputProps: {
                                    maxLength: 255,
                                    autoCorrect: "off",
                                    autoComplete: "false"
                                }
                            }
                        }}
                        error={!!formValidation.email}
                        helperText={<MprcStringToHtml text={formValidation?.email} />}
                    />

                    {
                        register.customFields!.length > 0 &&
                        <Grid container columnSpacing={4}>
                            {
                                register!.customFields?.map((item, i) => (
                                    <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                        <CustomFieldInput
                                            field={item}
                                            onChange={(value, _, isValid) => {
                                                handleUpdateCustomField(item, value, isValid);
                                            }}
                                            error={!!formValidation.customFields[item.fieldId]}
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    }

                    {
                        settings.workplaceRequired && settings.isWorkplaceFreeText
                            ? (
                                <TextField
                                    fullWidth
                                    required
                                    name="workplaceText"
                                    label={settings.workplaceLabel}
                                    margin="dense"
                                    value={register.workplaceText}
                                    onChange={(e) => handleChange(e)}
                                    slotProps={{
                                        input: {
                                            inputProps: { maxLength: 255 }
                                        }
                                    }}
                                    error={!!formValidation.workplaceText}
                                    helperText={<MprcStringToHtml text={formValidation?.workplaceText} />}
                                />
                            ) : settings.workplaceRequired
                                ?
                                <WorkplaceAutoComplete
                                    label={settings.workplaceLabel}
                                    multiSelect={false}
                                    onError={e => setViewErrors(e)}
                                    selectedItems={register.workplaceId != undefined ?
                                        [{ key: register.workplaceId.workplaceId, label: register.workplaceId.name, canDelete: true } as AutoCompleteDataModel] :
                                        []}
                                    onChange={(items) => {
                                        if (items.length > 0) handleUpdateRecord("workplaceId", { workplaceId: items[0].key, name: items[0].label });
                                        else handleUpdateRecord("workplaceId", null);
                                    }}
                                    error={!!formValidation.workplaceId}
                                    helperText={<MprcStringToHtml text={formValidation?.workplaceId} />}
                                />
                                : null
                    }
                </MprcContainer>

                <MprcContainer>
                    <MprcFormSubHeader
                        margin="dense"
                        title={t("Auth.Register.PasswordSubtitle")}
                    />
                    <Grid container spacing={4}>
                        <Grid size="grow">
                            <TextField
                                fullWidth
                                required
                                label={t("Auth.Register.PasswordLabel")}
                                name={"password"}
                                type={showPass ? undefined : "password"}
                                value={register.password}
                                onChange={(e) => handleChange(e)}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            maxLength: 64,
                                            autoCorrect: "off",
                                            autoComplete: "new-password"
                                        },
                                        endAdornment: <FontAwesomeIcon
                                            className={AppGlobalClassKeys.clickable}
                                            icon={showPass ? faEye : faEyeSlash}
                                            fixedWidth
                                            onClick={() => setShowPass(!showPass)}
                                        />
                                    }
                                }}
                                error={!!formValidation.password}
                                helperText={<MprcStringToHtml text={formValidation.password} />}
                            />
                        </Grid>
                        <Grid size="grow">
                            <TextField
                                fullWidth
                                required
                                label={t("Auth.Register.PasswordConfirmLabel")}
                                name={"confirmPassword"}
                                type={showPass ? undefined : "password"}
                                value={register.confirmPassword}
                                onChange={(e) => handleChange(e)}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            maxLength: 64,
                                            autoCorrect: "off",
                                            autoComplete: "new-password"
                                        },
                                        endAdornment: <FontAwesomeIcon
                                            className={AppGlobalClassKeys.clickable}
                                            icon={showPass ? faEye : faEyeSlash}
                                            fixedWidth
                                            onClick={() => setShowPass(!showPass)}
                                        />
                                    }
                                }}
                                error={!!formValidation.confirmPassword}
                                helperText={<MprcStringToHtml text={formValidation?.confirmPassword} />}
                            />
                        </Grid>
                    </Grid>
                </MprcContainer>

                {
                    settings.agreementMustBeAssigned &&
                    <MprcContainer>
                        {
                            readAgree
                                ? (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="agreement"
                                                checked={agree}
                                                onChange={
                                                    (e) => {
                                                        setAgreement((e.target as HTMLInputElement).checked);
                                                    }
                                                }
                                                value="agreement"
                                            />
                                        }
                                        label={
                                            <MprcLink onClick={() => setOpenAgree(true)} title={t("Auth.Register.ViewAgreement")}>
                                                {agree ? t("Auth.Register.AgreementRead") : t("Auth.Register.AgreementNotRead")}
                                            </MprcLink>
                                        }
                                    />
                                )
                                : (
                                    <MprcButton variant="contained" color="info" onClick={() => setOpenAgree(true)}>
                                        {t("Auth.Register.AssignAgreement")}
                                    </MprcButton>
                                )
                        }

                        {
                            openAgree &&
                            <AgreementCheckbox
                                onClose={
                                    (agree) => {
                                        setOpenAgree(false);
                                        setReadAgree(true);
                                        setAgreement(agree);
                                    }
                                }
                            />
                        }
                    </MprcContainer>
                }

            </MprcContainer >
        );
    };

    const renderActions = () => {
        return (
            <Grid container spacing={2} justifyContent="flex-end">
                <Grid>
                    <ButtonCancel variant="contained" onClick={() => onClose()} />
                </Grid>
                <Grid>
                    <MprcButton
                        variant="contained"
                        color="success"
                        disabled={!canSave}
                        onClick={() => setSaving(true)}
                    >
                        {t("Auth.Register.RegisterButtonText")}
                    </MprcButton>
                </Grid>
            </Grid>
        );
    };

    if (initializeErrors)
        return <MprcRequestErrorDialog error={initializeErrors} onClose={onClose} />;

    if (loading)
        return <MprcRequestIndicator type="request" />;

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
        >
            <MprcDialogTitleClose
                shrinkPadding
                title={t("Auth.Register.FormTitle")}
                onClose={() => onClose()}
            />
            <DialogContent >
                <MprcRequestIndicator visible={saving} type="post" />
                {
                    viewErrors
                        ? <MprcRequestErrorDialog error={viewErrors} onClose={() => setViewErrors(null)} />
                        : renderForm()
                }
            </DialogContent>
            <DialogActions>
                {renderActions()}
            </DialogActions>
        </Dialog>
    );
};
